import React, { useState, useEffect, useCallback, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AdminProjectContext, AdminProjectContextType } from "../../../contexts/AdminProjectContext";
import { Project } from "../../../Models/Project";
import { useProjects } from "../../../api/projects/useProjects";
import { useProjectParticipants } from "../../../api/projects/useProjectParticipants";
import { queryKeys } from "../../../api/queryKeys";
import { SELECTED_PROJECT_STORAGE_KEY } from "../../../utils/constant";

interface AdminProjectProviderProps {
    children: ReactNode;
    initialProjects?: Project[];
    initialSelectedProject?: Project | null;
}

export const AdminProjectProvider = ({
    children,
    initialSelectedProject = null,
}: AdminProjectProviderProps) => {
    const [selectedProject, setSelectedProjectState] = useState<Project | null>(initialSelectedProject);
    const [reloadDashboard, setReloadDashboard] = useState<boolean>(false);
    const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

    const queryClient = useQueryClient();

    // Fetch projects using React Query
    const {
        data: projects = [],
        isLoading: loading,
        error: projectsError,
        refetch: refetchProjects
    } = useProjects();

    // Fetch participants for selected project
    const {
        data: participants = [],
        refetch: refetchParticipants
    } = useProjectParticipants(selectedProject?.uuid);

    // Convert error to string
    const error = projectsError ? (projectsError as Error).message : null;

    // Restore selected project from localStorage on mount
    useEffect(() => {
        const savedProjectUuid = localStorage.getItem(SELECTED_PROJECT_STORAGE_KEY);
        if (savedProjectUuid && projects.length > 0 && !selectedProject) {
            const savedProject = projects.find(p => p.uuid === savedProjectUuid);
            if (savedProject) {
                setSelectedProjectState(savedProject);
            } else if (projects.length > 0) {
                setSelectedProjectState(projects[0]);
            }
        } else if (projects.length > 0 && !selectedProject) {
            setSelectedProjectState(projects[0]);
        }
    }, [projects, selectedProject]);

    // Update selected project with participants when they're loaded
    const selectedProjectWithParticipants = selectedProject ? new Project({
        ...selectedProject,
        participants: participants.length,
        Users: participants,
    }) : null;

    const setSelectedProject = useCallback((project: Project | null) => {
        setSelectedProjectState(project);
        if (project?.uuid) {
            localStorage.setItem(SELECTED_PROJECT_STORAGE_KEY, project.uuid);
        } else {
            localStorage.removeItem(SELECTED_PROJECT_STORAGE_KEY);
        }
    }, []);

    // Load participants for a specific project (returns cached data from React Query)
    const loadParticipantsForProject = useCallback(async (projectId: string, forceRefresh: boolean = false): Promise<any[]> => {
        if (forceRefresh) {
            await queryClient.invalidateQueries({ queryKey: queryKeys.projects.participants(projectId) });
        }
        // Return current cache or empty array - React Query will fetch if needed
        const cached = queryClient.getQueryData<any[]>(queryKeys.projects.participants(projectId));
        return cached || [];
    }, [queryClient]);

    // Refresh all projects
    const refreshProjects = useCallback(async () => {
        await queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
        await refetchProjects();
        setRefreshTrigger(prev => prev + 1);
    }, [queryClient, refetchProjects]);

    // Select a project and ensure participants are loaded
    const selectProjectWithParticipants = useCallback(async (project: Project) => {
        setSelectedProject(project);
        // React Query will automatically fetch participants when selectedProject changes
    }, [setSelectedProject]);

    // Handle reloadDashboard trigger
    useEffect(() => {
        if (reloadDashboard) {
            refreshProjects().then(() => setReloadDashboard(false));
        }
    }, [reloadDashboard, refreshProjects]);

    const contextValue: AdminProjectContextType = {
        projects,
        selectedProject: selectedProjectWithParticipants,
        reloadDashboard,
        refreshTrigger,
        setSelectedProject,
        setReloadDashboard,
        refreshProjects,
        loadParticipantsForProject,
        selectProjectWithParticipants,
        loading,
        error,
    };

    return (
        <AdminProjectContext.Provider value={contextValue}>
            {children}
        </AdminProjectContext.Provider>
    );
};
